import {
  getEmailExclusionJobs,
  getEmailExclusionJob,
} from 'services/acg/phase-1';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QUERY_KEY from 'constants/query-key';
import { SOCKET_EVENTS, SOCKET_NAME, useSocket } from '../../../socket';
import type {
  CampaignFile,
  EmailExclusionJob,
  ExclusionFile,
  ListEmailExclusionJobsResponse,
} from 'containers/AutomatedCampaignGeneration/Phase1/EmailExclusion/types';
import type { User } from 'containers/AutomatedCampaignGeneration/Phase1/CampaignGeneration/types';
import _ from 'lodash';
import objectPath from 'object-path';
import { useEffect } from 'react';

export const useListEmailExclusionJobs = (page, limit) => {
  return useQuery(
    [QUERY_KEY.ACG.PHASE_1.LIST_EMAIL_EXCLUSION_JOBS, { page, limit }],
    () => getEmailExclusionJobs(page, limit),
  );
};

export const useGetEmailExclusionJob = id => {
  return useQuery([QUERY_KEY.ACG.PHASE_1.GET_EMAIL_EXCLUSION_JOB, { id }], () =>
    getEmailExclusionJob(id),
  );
};

export const useEmailExclusionJobSubscription = () => {
  const queryClient = useQueryClient();
  function handleOnEmailExclusionJobCreated(data: {
    data: EmailExclusionJob,
    user: User,
  }) {
    queryClient.setQueriesData(
      {
        queryKey: [
          QUERY_KEY.ACG.PHASE_1.LIST_EMAIL_EXCLUSION_JOBS,
          { page: 1 },
        ],
        type: 'active',
      },
      (oldData: ListEmailExclusionJobsResponse) => {
        if (!oldData) return oldData;
        const cloned = _.cloneDeep(oldData);
        objectPath.set(cloned, `data`, _.take([data.data, ...cloned.data], 10));
        objectPath.set(cloned, `users.${data.user.id}`, data.user);
        objectPath.set(cloned, 'total', cloned.total + 1);
        return cloned;
      },
    );
    void queryClient.invalidateQueries({
      queryKey: [QUERY_KEY.ACG.PHASE_1.LIST_EMAIL_EXCLUSION_JOBS],
      type: 'inactive',
    });
  }

  function handleOnEmailExclusionJobUpdated(data: {
    data: EmailExclusionJob,
    user: User,
  }) {
    queryClient.setQueriesData(
      {
        queryKey: [QUERY_KEY.ACG.PHASE_1.LIST_EMAIL_EXCLUSION_JOBS],
      },
      (oldData: ListEmailExclusionJobsResponse) => {
        if (!oldData) return oldData;
        const cloned = _.cloneDeep(oldData);
        const jobIndex = cloned.data.findIndex(job => job.id === data.data.id);
        if (jobIndex !== -1) {
          objectPath.set(cloned, `data.${jobIndex}`, data.data);
          objectPath.set(cloned, `users.${data.user.id}`, data.user);
        }
        return cloned;
      },
    );
    queryClient.setQueriesData(
      {
        queryKey: [
          QUERY_KEY.ACG.PHASE_1.GET_EMAIL_EXCLUSION_JOB,
          { id: data.data.id },
        ],
      },
      (oldData: { data: EmailExclusionJob, user: User }) => {
        if (!oldData) return oldData;
        const cloned = _.cloneDeep(oldData);
        objectPath.set(cloned, `data`, data.data);
        objectPath.set(cloned, `users.${data.user.id}`, data.user);
        return cloned;
      },
    );
  }

  function handleExclusionFileOrCampaignFileUpdated(
    type: 'exclusion' | 'campaign',
  ) {
    return function(data: ExclusionFile | CampaignFile) {
      queryClient.setQueriesData(
        {
          queryKey: [QUERY_KEY.ACG.PHASE_1.LIST_EMAIL_EXCLUSION_JOBS],
        },
        (oldData: ListEmailExclusionJobsResponse) => {
          if (!oldData) return oldData;
          const cloned = _.cloneDeep(oldData);
          const jobIndex = cloned.data.findIndex(
            job => job.id === data.exclusionJobId,
          );
          if (jobIndex === -1) return cloned;
          const job = cloned.data[jobIndex];
          const key = type === 'exclusion' ? 'exclusionFiles' : 'campaignFiles';
          const fileIndex = job[key].findIndex(file => file.id === data.id);
          if (fileIndex === -1) return cloned;
          objectPath.set(cloned, `data.${jobIndex}.${key}.${fileIndex}`, data);
          return cloned;
        },
      );
      queryClient.setQueriesData(
        {
          queryKey: [
            QUERY_KEY.ACG.PHASE_1.GET_EMAIL_EXCLUSION_JOB,
            { id: data.exclusionJobId },
          ],
        },
        (oldData: { data: EmailExclusionJob, user: User }) => {
          if (!oldData) return oldData;
          const cloned = _.cloneDeep(oldData);
          const key = type === 'exclusion' ? 'exclusionFiles' : 'campaignFiles';
          const fileIndex = cloned.data[key].findIndex(
            file => file.id === data.id,
          );
          if (fileIndex === -1) return cloned;
          objectPath.set(cloned, `data.${key}.${fileIndex}`, data);
          return cloned;
        },
      );
    };
  }

  const socket = useSocket(SOCKET_NAME.ACG_PHASE_1);
  useEffect(() => {
    socket.on(
      SOCKET_EVENTS.ACG_PHASE_1.EXCLUSION_JOB_CREATED,
      handleOnEmailExclusionJobCreated,
    );
    socket.on(
      SOCKET_EVENTS.ACG_PHASE_1.EXCLUSION_JOB_UPDATED,
      handleOnEmailExclusionJobUpdated,
    );
    socket.on(
      SOCKET_EVENTS.ACG_PHASE_1.EXCLUSION_JOB_EXCLUSION_FILE_UPDATED,
      handleExclusionFileOrCampaignFileUpdated('exclusion'),
    );
    socket.on(
      SOCKET_EVENTS.ACG_PHASE_1.EXCLUSION_JOB_CAMPAIGN_FILE_UPDATED,
      handleExclusionFileOrCampaignFileUpdated('campaign'),
    );
    return () => {
      socket.off(
        SOCKET_EVENTS.ACG_PHASE_1.EXCLUSION_JOB_CREATED,
        handleOnEmailExclusionJobCreated,
      );
      socket.off(
        SOCKET_EVENTS.ACG_PHASE_1.EXCLUSION_JOB_UPDATED,
        handleOnEmailExclusionJobUpdated,
      );
      socket.off(
        SOCKET_EVENTS.ACG_PHASE_1.EXCLUSION_JOB_EXCLUSION_FILE_UPDATED,
        handleExclusionFileOrCampaignFileUpdated('exclusion'),
      );
      socket.off(
        SOCKET_EVENTS.ACG_PHASE_1.EXCLUSION_JOB_CAMPAIGN_FILE_UPDATED,
        handleExclusionFileOrCampaignFileUpdated('campaign'),
      );
    };
  }, [socket]);
};
